// Bring a league (DIRECTION A13, scenario H10, PLAN-2026-10-18 §13.1): pick
// a league's file in the open league format, see what is in it, say which
// team is yours, and the league starts here in setup — its teams in open
// seats with their names, colors and badges, its finished seasons in the
// record book. What it did not take is said, a sentence each.
//
// Drawn through the kit (LOOKS.md Part 7).

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ImportLeagueResult, OpenLeagueFile } from '@fsp/domain';
import { importLeague, readLeagueFile } from '../../data/importFile';
import { useLeagues } from '../../data/LeaguesProvider';
import { Button, Callout, Choice, Stack, Text } from '../../kit/ui';
import { DetailSheet } from '../../templates/DetailSheet';

export function ImportLeagueSheet({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { reload } = useLeagues();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<OpenLeagueFile | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [mine, setMine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ImportLeagueResult | null>(null);

  const close = () => {
    setFile(null);
    setProblem(null);
    setMine(null);
    setDone(null);
    onClose();
  };

  const pick = async (picked: File | undefined) => {
    if (!picked) return;
    setProblem(null);
    setDone(null);
    const read = await readLeagueFile(picked);
    if (!read.ok) {
      setFile(null);
      setProblem(read.message);
      return;
    }
    if (read.file.scope !== 'league') {
      setFile(null);
      setProblem(
        "That is a team's file. A league starts from a league's file; a team's file can set your team's name and colors from Edit team.",
      );
      return;
    }
    setFile(read.file);
    setMine(null);
  };

  const bring = async () => {
    if (file === null || mine === null) return;
    setBusy(true);
    const made = await importLeague(file, mine, Date.now());
    setBusy(false);
    if (!made.ok) {
      setProblem(made.message);
      return;
    }
    reload();
    setDone(made.result);
  };

  const seasons = file?.league.seasons.filter((s) => s.complete).length ?? 0;
  return (
    <DetailSheet opened={opened} onClose={close} title="Bring a league">
      <Stack gap={3}>
        {done !== null ? (
          <>
            <Text block>
              {done.league.name} is here, in setup, with{' '}
              {done.seasons === 1 ? 'one past season' : `${done.seasons} past seasons`} in the
              record book. Each team waits for its manager: send each its own link from the league,
              and set the draft when everyone is in.
            </Text>
            {done.notes.length > 0 ? (
              <ul className="fsp-import-notes">
                {done.notes.map((n) => (
                  <li key={n}>
                    <Text size="sm" tone="secondary">
                      {n}
                    </Text>
                  </li>
                ))}
              </ul>
            ) : null}
            <Button
              variant="primary"
              icon="next"
              onClick={() => {
                close();
                navigate(`/l/${encodeURIComponent(done.league.id)}`);
              }}
            >
              Invite its managers
            </Button>
          </>
        ) : (
          <>
            <Text block tone="secondary">
              A league's file in the open league format, from this site or any site that writes it.
              The league starts here before its draft, its teams waiting for their managers, its
              past seasons in the record book. Nobody is signed up or written to: you send the
              links.
            </Text>
            <input
              ref={input}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(e) => {
                void pick(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <div>
              <Button icon="upload" onClick={() => input.current?.click()}>
                {file === null ? 'Choose a file' : 'Choose another file'}
              </Button>
            </div>
            {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
            {file !== null ? (
              <>
                <Text block>
                  <strong>{file.league.name}</strong> · {file.league.teams.length} teams ·{' '}
                  {seasons === 1 ? 'one finished season' : `${seasons} finished seasons`}
                </Text>
                <Choice
                  label="Which team is yours?"
                  placeholder="Your team"
                  searchable={false}
                  options={file.league.teams.map((t) => ({
                    value: t.key,
                    label: t.manager ? `${t.name} · ${t.manager}` : t.name,
                  }))}
                  value={mine}
                  onChange={setMine}
                />
                <div>
                  <Button
                    variant="primary"
                    icon="upload"
                    disabled={mine === null}
                    loading={busy}
                    onClick={() => void bring()}
                  >
                    Bring it here
                  </Button>
                </div>
              </>
            ) : null}
          </>
        )}
      </Stack>
    </DetailSheet>
  );
}
